import React, { useEffect, useState } from "react";
import { Settings, ShieldCheck, ListPlus, SlidersHorizontal, LayoutGrid, BellRing, X, Check, Search, KeyRound, Trash2, UserCog } from "lucide-react";
import client from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

const ROLE_LABELS = { supervisor: "مشرف", area_manager: "مدير منطقة", super_admin: "سوبر أدمن" };

function AccountEditModal({ u, onClose, onSaved, onOtp, t }) {
  const [form, setForm] = useState({
    full_name: u.full_name || "", username: u.username || "", email: u.email || "", phone: "",
    is_active: u.is_active !== false, admin_id: u.admin_id || "", area_manager_id: u.area_manager_id || "",
  });
  const [detail, setDetail] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [ams, setAms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [convAdmin, setConvAdmin] = useState("");
  const [convAm, setConvAm] = useState("");
  const isSup = u.role === "supervisor";
  const isAm = u.role === "area_manager";
  useEffect(() => {
    client.get(`/users/${u.id}/account`).then((r) => {
      setDetail(r.data);
      setForm((s) => ({ ...s, phone: r.data.phone || "", full_name: r.data.full_name || s.full_name, email: r.data.email || s.email }));
    }).catch(() => {});
    if (isSup || isAm) {
      client.get("/admins").then((r) => setAdmins(r.data || [])).catch(() => {});
      client.get("/area-managers").then((r) => setAms(r.data || [])).catch(() => {});
    }
  }, [u.id, isSup, isAm]);
  const convert = async () => {
    if (!convAdmin && !convAm) return;
    if (!window.confirm(t("ss_convert_confirm", { name: u.full_name || u.username }))) return;
    setSaving(true);
    try {
      const body = convAdmin ? { admin_id: convAdmin } : { area_manager_id: convAm };
      const r = await client.post(`/users/${u.id}/convert-to-supervisor`, body);
      toast.success(t("ss_convert_done", { am: r.data.area_manager_name || "" }), { duration: 9000 });
      onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || t("em_save_error")); }
    setSaving(false);
  };
  const save = async () => {
    setSaving(true);
    try {
      const body = { full_name: form.full_name, username: form.username, email: form.email, phone: form.phone, is_active: form.is_active };
      if (isSup) {
        if (form.admin_id) body.admin_id = form.admin_id;
        if (form.area_manager_id) body.area_manager_id = form.area_manager_id;
      }
      await client.patch(`/users/${u.id}/account`, body);
      toast.success(t("ss_acc_saved"));
      onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || t("em_save_error")); }
    setSaving(false);
  };
  const inp = "w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400";
  const lbl = "text-[11px] font-bold text-slate-500";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="account-edit-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h3 className="font-black text-slate-800 text-sm flex items-center gap-2">
            <UserCog size={16} className="text-slate-500" /> {t("ss_edit_account")}
            <span className="text-[10px] font-bold bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{ROLE_LABELS[u.role] || u.role}</span>
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100" data-testid="account-edit-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div><label className={lbl}>{t("ss_acc_fullname")}</label>
            <input value={form.full_name} onChange={(e) => setForm((s) => ({ ...s, full_name: e.target.value }))} className={inp} data-testid="account-edit-fullname" /></div>
          <div><label className={lbl}>{t("ss_acc_username")}</label>
            <input value={form.username} onChange={(e) => setForm((s) => ({ ...s, username: e.target.value }))} className={inp} dir="ltr" data-testid="account-edit-username" /></div>
          <div><label className={lbl}>{t("ss_acc_email")}</label>
            <input value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} className={inp} dir="ltr" data-testid="account-edit-email" /></div>
          <div><label className={lbl}>{t("ss_acc_phone")}</label>
            <input value={form.phone} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))} className={inp} dir="ltr" data-testid="account-edit-phone" /></div>
          {isSup && (
            <>
              <div><label className={lbl}>{t("ss_acc_linked_admin")}</label>
                <select value={form.admin_id} onChange={(e) => setForm((s) => ({ ...s, admin_id: e.target.value }))} className={inp} data-testid="account-edit-admin">
                  <option value="">—</option>
                  {admins.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select></div>
              <div><label className={lbl}>{t("ss_acc_linked_am")}</label>
                <select value={form.area_manager_id} onChange={(e) => setForm((s) => ({ ...s, area_manager_id: e.target.value }))} className={inp} data-testid="account-edit-am">
                  <option value="">—</option>
                  {ams.map((a) => <option key={a.id} value={a.id}>{a.full_name || a.username}</option>)}
                </select></div>
            </>
          )}
          <div className="flex items-center justify-between pt-1">
            <span className="text-sm font-bold text-slate-700">{t("ss_acc_active")}</span>
            <button onClick={() => setForm((s) => ({ ...s, is_active: !s.is_active }))} className={`relative w-11 h-6 rounded-full transition ${form.is_active ? "bg-green-500" : "bg-slate-300"}`} data-testid="account-edit-active">
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${form.is_active ? "start-[22px]" : "start-0.5"}`} />
            </button>
          </div>
          {detail && (
            <div className="bg-slate-50 rounded-xl p-3 space-y-2 text-xs" data-testid="account-detail-info">
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-500">{t("ss_acc_telegram")}</span>
                <span className={`font-black rounded-full px-2 py-0.5 text-[10px] ${detail.telegram_linked ? "bg-sky-100 text-sky-700" : "bg-slate-200 text-slate-500"}`} data-testid="account-tg-status">
                  {detail.telegram_linked ? t("ss_acc_tg_linked") : t("ss_acc_tg_not_linked")}
                </span>
              </div>
              {detail.otp_pending && <div className="text-[10px] font-bold text-amber-600" data-testid="account-otp-pending">⏳ {t("ss_acc_otp_pending")}</div>}
              <div>
                <div className="font-bold text-slate-500 mb-1">{t("ss_acc_branches")} ({(detail.branches || []).length})</div>
                {(detail.branches || []).length === 0 ? (
                  <span className="text-slate-400">{t("ss_acc_no_branches")}</span>
                ) : (
                  <div className="flex flex-wrap gap-1" data-testid="account-edit-branches">
                    {detail.branches.map((b) => (
                      <span key={b.id} className="bg-white border border-slate-200 rounded-full px-2 py-0.5 text-[10px] font-bold text-slate-600">{b.code ? `${b.code} — ` : ""}{b.name}</span>
                    ))}
                  </div>
                )}
              </div>
              {detail.last_login_at && <div className="text-[10px] text-slate-400">👁 {t("activity_last_seen")}: <span dir="ltr">{String(detail.last_seen_at || detail.last_login_at).slice(0, 16).replace("T", " ")}</span></div>}
            </div>
          )}
          {isAm && (
            <div className="border border-amber-200 bg-amber-50/50 rounded-xl p-3 space-y-2" data-testid="account-convert-section">
              <div className="text-xs font-black text-amber-800">{t("ss_convert_title")}</div>
              <p className="text-[10px] text-amber-700/80">{t("ss_convert_hint")}</p>
              <label className={lbl}>{t("ss_convert_existing")}</label>
              <select value={convAdmin} onChange={(e) => { setConvAdmin(e.target.value); if (e.target.value) setConvAm(""); }} className={inp} data-testid="account-convert-admin">
                <option value="">—</option>
                {admins.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <label className={lbl}>{t("ss_convert_new_under")}</label>
              <select value={convAm} onChange={(e) => { setConvAm(e.target.value); if (e.target.value) setConvAdmin(""); }} className={inp} data-testid="account-convert-am">
                <option value="">—</option>
                {ams.map((a) => <option key={a.id} value={a.id}>{a.full_name || a.username}</option>)}
              </select>
              <button onClick={convert} disabled={saving || (!convAdmin && !convAm)} className="w-full bg-amber-600 text-white text-xs font-black rounded-xl py-2 hover:bg-amber-700 disabled:opacity-40" data-testid="account-convert-btn">
                {t("ss_convert_btn")}
              </button>
            </div>
          )}
          <button onClick={() => onOtp(u)} className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-xl py-2.5 hover:bg-teal-100" data-testid="account-edit-reset-otp">
            <KeyRound size={13} /> {t("ss_reset_otp")}
          </button>
          <button onClick={save} disabled={saving} className="w-full bg-slate-800 text-white text-sm font-black rounded-xl py-2.5 hover:bg-slate-700 disabled:opacity-50" data-testid="account-edit-save">
            {saving ? t("saving") : t("save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function AccountLookup() {
  const { t } = useT();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const search = async () => {
    if (q.trim().length < 2) return;
    setBusy(true);
    try {
      const r = await client.get("/users/lookup", { params: { q: q.trim() } });
      setResults(r.data);
    } catch { toast.error(t("em_load_error")); }
    setBusy(false);
  };

  const sendOtp = async (u) => {
    if (!window.confirm(t("ss_otp_confirm", { name: u.full_name || u.username }))) return;
    try {
      const r = await client.post(`/users/${u.id}/reset-otp`);
      const chs = (r.data.channels || []).map((c) => t(`ss_ch_${c}`)).join("، ");
      toast.success(t("ss_otp_sent", { channels: chs }), { duration: 8000 });
      if (r.data.user_telegram_contact) {
        window.prompt(t("ss_otp_tg_contact"), r.data.user_telegram_contact);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || t("em_save_error")); }
  };

  const delLogin = async (u) => {
    if (!window.confirm(t("ss_delete_login_confirm", { name: u.full_name || u.username }))) return;
    try {
      await client.delete(`/users/${u.id}/login`);
      toast.success(t("ss_login_deleted"));
      search();
    } catch (e) { toast.error(e?.response?.data?.detail || t("em_save_error")); }
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-slate-400">{t("ss_acc_click_hint")}</p>
      <div className="flex gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder={t("ss_lookup_placeholder")}
          className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
          data-testid="account-lookup-input"
        />
        <button onClick={search} disabled={busy || q.trim().length < 2} className="bg-slate-700 text-white text-sm font-bold rounded-xl px-4 disabled:opacity-40" data-testid="account-lookup-btn">
          <Search size={15} />
        </button>
      </div>
      {results !== null && (results.length === 0 ? (
        <div className="text-xs text-slate-400" data-testid="account-lookup-empty">{t("ss_lookup_no_results")}</div>
      ) : (
        <div className="space-y-2">
          {results.map((u) => (
            <div key={u.id} className="border border-slate-200 rounded-xl p-3 space-y-1.5" data-testid={`account-result-${u.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <button onClick={() => setEditing(u)} className="text-sm font-black text-sky-700 hover:underline text-start" data-testid={`account-name-${u.id}`}>
                  {u.full_name || u.username}
                  <span className="ms-2 text-[10px] font-bold bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{ROLE_LABELS[u.role] || u.role}</span>
                  {u.admin_missing && <span className="ms-1 text-[10px] font-black bg-rose-100 text-rose-700 rounded-full px-2 py-0.5">{t("ss_orphan_account")}</span>}
                  {u.is_active === false && <span className="ms-1 text-[10px] font-bold bg-slate-200 text-slate-500 rounded-full px-2 py-0.5">{t("ss_inactive")}</span>}
                </button>
                <div className="flex gap-1.5">
                  <button onClick={() => sendOtp(u)} className="inline-flex items-center gap-1 text-[11px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-lg px-2 py-1 hover:bg-teal-100" data-testid={`account-reset-otp-${u.id}`}>
                    <KeyRound size={12} /> {t("ss_reset_otp")}
                  </button>
                  {u.role === "supervisor" && (
                    <button onClick={() => delLogin(u)} className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2 py-1 hover:bg-rose-100" data-testid={`account-delete-${u.id}`}>
                      <Trash2 size={12} /> {t("ss_delete_login")}
                    </button>
                  )}
                </div>
              </div>
              <div className="text-xs text-slate-500 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5">
                <span>👤 {u.username}</span>
                {u.email && <span dir="ltr" className="text-start">✉️ {u.email}</span>}
                {u.admin_name && <span>🧑‍💼 {t("ss_linked_supervisor")}: {u.admin_name}</span>}
                {u.area_manager_name && <span>📍 {t("ss_linked_am")}: {u.area_manager_name}</span>}
                {u.last_seen_at && <span>👁 {t("activity_last_seen")}: <span dir="ltr">{String(u.last_seen_at).slice(0, 16).replace("T", " ")}</span></span>}
              </div>
            </div>
          ))}
        </div>
      ))}
      {editing && <AccountEditModal u={editing} onClose={() => setEditing(null)} onSaved={search} onOtp={sendOtp} t={t} />}
    </div>
  );
}

const Card = ({ icon: Icon, title, children, testid }) => (
  <div className="bg-white rounded-2xl card-shadow p-5" data-testid={testid}>
    <h2 className="font-black text-slate-800 text-sm flex items-center gap-2 mb-4">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-slate-500 to-slate-700 text-white"><Icon size={17} /></span>
      {title}
    </h2>
    {children}
  </div>
);

const Toggle = ({ value, onChange, testid }) => (
  <button onClick={() => onChange(!value)} className={`relative w-11 h-6 rounded-full transition ${value ? "bg-green-500" : "bg-slate-300"}`} data-testid={testid}>
    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${value ? "start-[22px]" : "start-0.5"}`} />
  </button>
);

export function EMFieldsManager({ compact = false }) {
  const { t } = useT();
  const [fields, setFields] = useState([]);
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState(null); // {id, name}
  const load = () => client.get("/em/fields").then((r) => setFields(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!newName.trim()) return;
    try { await client.post("/em/fields", { name: newName.trim() }); setNewName(""); load(); toast.success(t("em_saved")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("em_save_error")); }
  };
  const rename = async () => {
    try { await client.patch(`/em/fields/${renaming.id}`, { name: renaming.name }); setRenaming(null); load(); toast.success(t("em_saved")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("em_save_error")); }
  };
  const del = async (fid) => {
    try { await client.delete(`/em/fields/${fid}`); load(); toast.success(t("em_saved")); }
    catch { toast.error(t("em_save_error")); }
  };
  const setLevel = async (f) => {
    const next = f.alert_level === "critical" ? "normal" : "critical";
    try { await client.patch(`/em/fields/${f.id}`, { alert_level: next }); load(); toast.success(t("em_saved")); }
    catch { toast.error(t("em_save_error")); }
  };
  return (
    <div className="space-y-2" data-testid="em-fields-manager">
      <p className="text-[11px] text-slate-400">{t("ss_fields_hint")}</p>
      <div className="flex gap-2">
        <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t("em_field_name_ph")} className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="ss-new-field-input" />
        <button onClick={add} className="bg-sky-600 text-white text-xs font-bold rounded-xl px-4 hover:bg-sky-700" data-testid="ss-add-field-btn">+ {t("em_add_field")}</button>
      </div>
      <div className={`space-y-1 ${compact ? "max-h-48" : "max-h-72"} overflow-y-auto`}>
        {fields.length === 0 && <div className="text-xs text-slate-400 py-2">{t("em_no_fields")}</div>}
        {fields.map((f) => (
          <div key={f.id} className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2 text-sm">
            {renaming?.id === f.id ? (
              <>
                <input value={renaming.name} onChange={(e) => setRenaming((s) => ({ ...s, name: e.target.value }))} className="flex-1 px-2 py-1 rounded-lg border border-sky-300 text-sm" data-testid={`ss-rename-input-${f.id}`} />
                <button onClick={rename} className="text-green-600 p-1 rounded hover:bg-green-50" data-testid={`ss-rename-save-${f.id}`}><Check size={14} /></button>
                <button onClick={() => setRenaming(null)} className="text-slate-400 p-1"><X size={14} /></button>
              </>
            ) : (
              <>
                <span className="flex-1 font-bold text-slate-700">{f.name}</span>
                <button onClick={() => setLevel(f)} title={t("em_field_level_hint")}
                  className={`text-[10px] font-black rounded-full px-2 py-1 transition ${f.alert_level === "critical" ? "bg-rose-100 text-rose-600" : "bg-slate-200 text-slate-500 hover:bg-slate-300"}`}
                  data-testid={`ss-field-level-${f.id}`}>
                  {f.alert_level === "critical" ? `🚨 ${t("em_field_level_critical")}` : t("em_field_level_normal")}
                </button>
                <button onClick={() => setRenaming({ id: f.id, name: f.name })} className="text-[11px] font-bold text-sky-600 hover:underline" data-testid={`ss-rename-${f.id}`}>{t("edit")}</button>
                <button onClick={() => del(f.id)} className="text-rose-500 hover:bg-rose-50 rounded-lg p-1" data-testid={`ss-del-field-${f.id}`}><X size={14} /></button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const FIXED_PERMS = ["perm_view_all", "perm_manage_ams", "perm_system_settings"];
const EDITABLE_PERMS = [
  ["perm_manage_sups", "manage_sups"],
  ["perm_manage_branches", "manage_branches"],
  ["perm_add_employees", "add_employees"],
  ["perm_delete_employees", "delete_employees"],
  ["perm_licenses_edit", "licenses_edit"],
  ["perm_dayoff", "dayoff"],
  ["perm_em_approve", "em_approve"],
  ["perm_em_requests", "em_requests"],
];

export default function SystemSettings() {
  const { t } = useT();
  const [s, setS] = useState(null);
  const [info, setInfo] = useState(null);
  useEffect(() => {
    client.get("/settings").then((r) => setS(r.data)).catch(() => {});
    client.get("/settings/system-info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const patch = async (body) => {
    try {
      const res = await client.patch("/settings", body);
      setS(res.data);
      toast.success(t("em_saved"));
    } catch { toast.error(t("em_save_error")); }
  };

  const mods = [["employees", "chooser_module_employees_title"], ["licenses", "ss_mod_licenses"], ["dayoff", "ss_mod_dayoff"], ["empmgmt", "chooser_module_empmgmt_title"]];
  const pill = (v) => (v ? <span className="text-green-600 font-black">✓</span> : <span className="text-slate-300 font-black">—</span>);

  return (
    <div className="space-y-6" data-testid="system-settings-page">
      <div>
        <h1 className="text-2xl font-black text-slate-800 flex items-center gap-2">
          <Settings className="text-slate-600" size={24} /> {t("ss_title")}
        </h1>
        <p className="text-sm text-slate-500 mt-1">{t("ss_intro")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card icon={Search} title={t("ss_account_lookup")} testid="ss-card-account-lookup">
          <AccountLookup />
        </Card>

        <Card icon={ListPlus} title={t("ss_dynamic_fields")} testid="ss-card-dynamic-fields">
          <EMFieldsManager />
        </Card>

        <Card icon={BellRing} title={t("ss_notifications")} testid="ss-card-notifications">
          {!s ? <div className="text-xs text-slate-400">{t("loading")}</div> : (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-bold text-slate-700">{t("ss_digest_email")}</span>
                <Toggle value={s.digest_email_enabled} onChange={(v) => patch({ digest_email_enabled: v })} testid="ss-toggle-email" />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="font-bold text-slate-700">{t("ss_digest_telegram")}</span>
                <Toggle value={s.digest_telegram_enabled} onChange={(v) => patch({ digest_telegram_enabled: v })} testid="ss-toggle-telegram" />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="font-bold text-slate-700">{t("ss_default_warning")}</span>
                <select value={s.default_warning_days} onChange={(e) => patch({ default_warning_days: Number(e.target.value) })} className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid="ss-warning-days">
                  {[7, 14, 30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>{d} {t("ss_days")}</option>)}
                </select>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="font-bold text-slate-700">{t("ss_restore_window")}</span>
                <select value={s.restore_window_days} onChange={(e) => patch({ restore_window_days: Number(e.target.value) })} className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid="ss-restore-window">
                  {[30, 60, 90].map((d) => <option key={d} value={d}>{d} {t("ss_days")}</option>)}
                </select>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="font-bold text-slate-700">{t("ss_otp_expiry")}</span>
                <select value={s.otp_expiry_minutes} onChange={(e) => patch({ otp_expiry_minutes: Number(e.target.value) })} className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid="ss-otp-expiry">
                  {[10, 15, 30].map((d) => <option key={d} value={d}>{d} {t("ss_minutes")}</option>)}
                </select>
              </div>
              <p className="text-[11px] text-slate-400">{t("ss_notif_hint")}</p>
            </div>
          )}
        </Card>

        <Card icon={LayoutGrid} title={t("ss_modules")} testid="ss-card-modules">
          {!s ? <div className="text-xs text-slate-400">{t("loading")}</div> : (
            <div className="space-y-3">
              {mods.map(([k, label]) => (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span className="font-bold text-slate-700">{t(label)}</span>
                  <Toggle value={s.modules[k]} onChange={(v) => patch({ modules: { ...s.modules, [k]: v } })} testid={`ss-toggle-mod-${k}`} />
                </div>
              ))}
              <p className="text-[11px] text-slate-400">{t("ss_modules_hint")}</p>
            </div>
          )}
        </Card>

        <Card icon={SlidersHorizontal} title={t("ss_system")} testid="ss-card-system">
          {!info ? <div className="text-xs text-slate-400">{t("loading")}</div> : (
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(info).map(([k, v]) => (
                <div key={k} className="bg-slate-50 rounded-xl p-2.5" data-testid={`ss-info-${k}`}>
                  <div className="text-lg font-black text-slate-800">{v}</div>
                  <div className="text-[10px] font-bold text-slate-500">{t(`ss_info_${k}`)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card icon={ShieldCheck} title={t("ss_permissions")} testid="ss-card-permissions">
          {!s ? <div className="text-xs text-slate-400">{t("loading")}</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500 border-b border-slate-100">
                    <th className="p-1.5 text-start font-bold">{t("ss_perm_capability")}</th>
                    <th className="p-1.5 font-bold">{t("role_super_admin")}</th>
                    <th className="p-1.5 font-bold">{t("role_area_manager")}</th>
                    <th className="p-1.5 font-bold">{t("role_supervisor")}</th>
                  </tr>
                </thead>
                <tbody>
                  {EDITABLE_PERMS.map(([label, key]) => (
                    <tr key={key} className="border-b border-slate-50">
                      <td className="p-1.5 font-semibold text-slate-700">{t(label)}</td>
                      <td className="p-1.5 text-center">{pill(true)}</td>
                      {["area_manager", "supervisor"].map((role) => {
                        const v = !!s.permissions?.[key]?.[role];
                        return (
                          <td key={role} className="p-1.5 text-center">
                            <button
                              onClick={() => patch({ permissions: { ...s.permissions, [key]: { ...s.permissions[key], [role]: !v } } })}
                              className={`w-8 h-6 rounded-lg font-black text-sm transition ${v ? "bg-green-100 text-green-600 hover:bg-green-200" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`}
                              data-testid={`ss-perm-${key}-${role}`}
                            >
                              {v ? "✓" : "—"}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {FIXED_PERMS.map((label) => (
                    <tr key={label} className="border-b border-slate-50 opacity-70">
                      <td className="p-1.5 font-semibold text-slate-500">{t(label)} 🔒</td>
                      <td className="p-1.5 text-center">{pill(true)}</td>
                      <td className="p-1.5 text-center">{pill(false)}</td>
                      <td className="p-1.5 text-center">{pill(false)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-slate-400 mt-2">{t("ss_perms_edit_hint")}</p>
        </Card>
      </div>
    </div>
  );
}
